import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const SnakeLotto = () => {
  const [gameState, setGameState] = useState('waiting'); // waiting, playing, finished, gameOver
  const [collectedNumbers, setCollectedNumbers] = useState([]);
  const [score, setScore] = useState(0);
  const [finalNumbers, setFinalNumbers] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [gridSize] = useState(12);
  
  // Używamy useRef dla płynnej animacji
  const snakeRef = useRef([{ x: 5, y: 5 }]);
  const directionRef = useRef({ x: 0, y: 0 });
  const foodRef = useRef({ x: 0, y: 0, number: 0, isGolden: false, isSpecial: false });
  const lastTimeRef = useRef(0);
  const animationIdRef = useRef();
  const touchStartRef = useRef({ x: 0, y: 0 });
  const gameStateRef = useRef('waiting');
  
  // Stan do renderowania (aktualizowany tylko gdy potrzeba)
  const [renderSnake, setRenderSnake] = useState([{ x: 5, y: 5 }]);
  const [renderFood, setRenderFood] = useState({ x: 0, y: 0, number: 0, isGolden: false, isSpecial: false });
  
  const speed = 300; // ms per move - płynniejszy ruch

  const updateGameState = useCallback((nextState) => {
    gameStateRef.current = nextState;
    setGameState(nextState);
  }, []);

  // Generuj nowe jabłko z liczbą
  const generateFood = useCallback(() => {
    const availableNumbers = [];
    for (let i = 1; i <= 49; i++) {
      if (!collectedNumbers.includes(i)) {
        availableNumbers.push(i);
      }
    }
    
    if (availableNumbers.length === 0) {
      return { x: 0, y: 0, number: 1, isGolden: false, isSpecial: false, isHidden: false };
    }
    
    let x, y;
    let attempts = 0;
    do {
      x = Math.floor(Math.random() * gridSize);
      y = Math.floor(Math.random() * gridSize);
      attempts++;
    } while (snakeRef.current.some(segment => segment.x === x && segment.y === y) && attempts < 50);
    
    const number = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
    const rand = Math.random();
    let isGolden = false;
    let isSpecial = false;
    let isHidden = false;
    
    if (rand < 0.15) { // 15% szans na ukrytą liczbę ze znakiem zapytania
      isHidden = true;
    } else if (rand < 0.25) { // 10% szans na specjalną gwiazdkę
      isSpecial = true;
    } else if (rand < 0.4) { // 15% szans na złotą kulę
      isGolden = true;
    }
    
    return { x, y, number, isGolden, isSpecial, isHidden };
  }, [collectedNumbers, gridSize]);

  // Aktualizuj renderowanie
  const updateRender = () => {
    setRenderSnake([...snakeRef.current]);
    setRenderFood({...foodRef.current});
  };

  // Główna pętla gry z requestAnimationFrame
  const gameLoop = useCallback((currentTime) => {
    if (gameStateRef.current !== 'playing') {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      return;
    }
    
    if (lastTimeRef.current === 0) {
      lastTimeRef.current = currentTime;
    }
    
    if (currentTime - lastTimeRef.current >= speed) {
      const currentSnake = [...snakeRef.current];
      const head = { ...currentSnake[0] };
      
      // Sprawdź czy wąż się porusza
      if (directionRef.current.x === 0 && directionRef.current.y === 0) {
        animationIdRef.current = requestAnimationFrame(gameLoop);
        return;
      }
      
      head.x += directionRef.current.x;
      head.y += directionRef.current.y;
      
      // Sprawdź kolizje ze ścianami
      if (head.x < 0 || head.x >= gridSize || head.y < 0 || head.y >= gridSize) {
        updateGameState('gameOver');
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current);
          animationIdRef.current = null;
        }
        return;
      }
      
      // Sprawdź kolizje z sobą (ogon zostanie usunięty, jeśli wąż nie zje jabłka)
      const willEat = head.x === foodRef.current.x && head.y === foodRef.current.y;
      const bodyToCheck = willEat ? currentSnake : currentSnake.slice(0, -1);
      if (bodyToCheck.some((segment) => segment.x === head.x && segment.y === head.y)) {
        updateGameState('gameOver');
        if (animationIdRef.current) {
          cancelAnimationFrame(animationIdRef.current);
          animationIdRef.current = null;
        }
        return;
      }
      
      currentSnake.unshift(head);
      
      // Sprawdź czy zjadł jabłko
      if (head.x === foodRef.current.x && head.y === foodRef.current.y) {
        const points = foodRef.current.isHidden ? 150 : foodRef.current.isSpecial ? 100 : foodRef.current.isGolden ? 50 : 10;
        
        setCollectedNumbers(prev => {
          const newNumbers = [...prev, foodRef.current.number];
          
          // Jeśli zebrał 6 liczb, zakończ grę
          if (newNumbers.length >= 6) {
            updateGameState('finished');
            setFinalNumbers(newNumbers.sort((a, b) => a - b));
            setShowConfetti(true);
            if (animationIdRef.current) {
              cancelAnimationFrame(animationIdRef.current);
              animationIdRef.current = null;
            }
            return newNumbers;
          }
          
          return newNumbers;
        });
        
        setScore(prev => prev + points);
        
        // Wygeneruj nowe jabłko
        const newFood = generateFood();
        foodRef.current = newFood;
        setRenderFood(newFood);
      } else {
        currentSnake.pop(); // Usuń ogon jeśli nie zjadł jabłka
      }
      
      snakeRef.current = currentSnake;
      setRenderSnake([...currentSnake]);
      lastTimeRef.current = currentTime;
    }
    
    if (gameStateRef.current === 'playing') {
      animationIdRef.current = requestAnimationFrame(gameLoop);
    }
  }, [gridSize, generateFood, speed, updateGameState]);

  // Inicjalizuj grę
  const initGame = () => {
    // Zatrzymaj obecną animację
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }
    
    // Przywróć scrollowanie
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    
    const initialSnake = [{ x: 5, y: 5 }];
    snakeRef.current = initialSnake;
    directionRef.current = { x: 0, y: 0 };
    setRenderSnake(initialSnake);
    setCollectedNumbers([]);
    setScore(0);
    setFinalNumbers([]);
    updateGameState('waiting');
    setShowConfetti(false);
    
    // Wygeneruj pierwsze jabłko
    const availableNumbers = [];
    for (let i = 1; i <= 49; i++) {
      availableNumbers.push(i);
    }
    
    let x, y;
    do {
      x = Math.floor(Math.random() * gridSize);
      y = Math.floor(Math.random() * gridSize);
    } while (x === 5 && y === 5); // Nie na pozycji węża
    
    const number = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
    const newFood = { x, y, number, isGolden: Math.random() < 0.2, isSpecial: false, isHidden: false };
    foodRef.current = newFood;
    setRenderFood(newFood);
  };

  // Rozpocznij grę
  const startGame = useCallback(() => {
    if (gameStateRef.current !== 'waiting') return;
    directionRef.current = { x: 1, y: 0 };
    lastTimeRef.current = 0;
    updateGameState('playing');
  }, [updateGameState]);

  const handleContainerClick = () => {
    if (gameStateRef.current === 'waiting') {
      startGame();
    }
  };

  // Sterowanie kierunkiem
  const changeDirection = (newDirection) => {
    directionRef.current = newDirection;
  };

  // Obsługa klawiszy
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameStateRef.current === 'waiting') {
        startGame();
        return;
      }
      
      if (gameStateRef.current !== 'playing') return;
      
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (directionRef.current.y === 0) changeDirection({ x: 0, y: -1 });
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (directionRef.current.y === 0) changeDirection({ x: 0, y: 1 });
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (directionRef.current.x === 0) changeDirection({ x: -1, y: 0 });
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (directionRef.current.x === 0) changeDirection({ x: 1, y: 0 });
          break;
        default:
          // Nie rób nic dla innych klawiszy
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [startGame]);

  // Obsługa dotyku - jak joystick (obsługiwane również przez event listenery)
  const handleTouchStart = useCallback((e) => {
    if (gameStateRef.current === 'waiting') {
      startGame();
      return;
    }
    
    if (gameStateRef.current !== 'playing') return;
    
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, [startGame]);

  const handleTouchMove = useCallback((e) => {
    if (gameStateRef.current !== 'playing') return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    // Mniejszy próg dla płynniejszego sterowania
    const threshold = 30;
    
    if (Math.abs(deltaX) > threshold || Math.abs(deltaY) > threshold) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Ruch poziomy
        if (deltaX > 0 && directionRef.current.x === 0) {
          changeDirection({ x: 1, y: 0 }); // Prawo
        } else if (deltaX < 0 && directionRef.current.x === 0) {
          changeDirection({ x: -1, y: 0 }); // Lewo
        }
      } else {
        // Ruch pionowy
        if (deltaY > 0 && directionRef.current.y === 0) {
          changeDirection({ x: 0, y: 1 }); // Dół
        } else if (deltaY < 0 && directionRef.current.y === 0) {
          changeDirection({ x: 0, y: -1 }); // Góra
        }
      }
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    // no-op for now
  }, []);

  // Globalne listenery dla urządzeń mobilnych
  useEffect(() => {
    const touchStartListener = (e) => {
      if (gameStateRef.current === 'playing') {
        e.preventDefault();
      }
      handleTouchStart(e);
    };

    const touchMoveListener = (e) => {
      if (gameStateRef.current !== 'playing') return;
      e.preventDefault();
      handleTouchMove(e);
    };

    const touchEndListener = (e) => {
      if (gameStateRef.current === 'playing') {
        e.preventDefault();
      }
      handleTouchEnd();
    };

    window.addEventListener('touchstart', touchStartListener, { passive: false });
    window.addEventListener('touchmove', touchMoveListener, { passive: false });
    window.addEventListener('touchend', touchEndListener, { passive: false });

    return () => {
      window.removeEventListener('touchstart', touchStartListener);
      window.removeEventListener('touchmove', touchMoveListener);
      window.removeEventListener('touchend', touchEndListener);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Blokuj scrollowanie podczas gry (bez anulowania pętli animacji)
  useEffect(() => {
    if (gameState === 'playing') {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [gameState]);

  // Pętla gry — uruchamiana tylko gdy gameState === 'playing'
  useEffect(() => {
    if (gameState !== 'playing') return undefined;

    lastTimeRef.current = 0;
    animationIdRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    };
  }, [gameState, gameLoop]);

  useEffect(() => {
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    gameStateRef.current = gameState;
  }, [gameState]);

  // Inicjalizuj grę przy starcie
  useEffect(() => {
    initGame();
  }, []); // Tylko raz przy mount

  // Funkcje zapisywania i kopiowania
  const copyNumbers = () => {
    try {
      const numbersText = `Snake Lotto: ${finalNumbers.join(', ')}`;
      navigator.clipboard.writeText(numbersText);
      alert('📋 Skopiowano do schowka!');
    } catch (error) {
      console.error('Błąd kopiowania:', error);
    }
  };

  const addToFavorites = () => {
    try {
      const favorite = {
        id: Date.now(),
        name: `🐍 Snake Lotto ${new Date().toLocaleDateString('pl-PL')}`,
        set: finalNumbers.join(', '),
        game: 'lotto',
        generatorType: 'snake-generator',
        date: new Date().toISOString(),
        score: score,
        numbers: [...finalNumbers]
      };
      
      const existingFavorites = JSON.parse(localStorage.getItem('favoriteSets') || '[]');
      const updatedFavorites = [favorite, ...existingFavorites];
      localStorage.setItem('favoriteSets', JSON.stringify(updatedFavorites));
      
      alert(`✅ Zestaw "${favorite.name}" został dodany do ulubionych!`);
    } catch (error) {
      console.error('Błąd dodawania do ulubionych:', error);
    }
  };

  return (
    <div 
      className={`snake-container min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 p-4 flex flex-col items-center justify-center relative overflow-hidden ${
        gameState === 'playing' ? 'touch-none' : ''
      }`}
      onClick={handleContainerClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        touchAction: gameState === 'playing' ? 'none' : 'auto'
      }}
    >
      {gameState === 'waiting' && (
        <div 
          className="absolute inset-0 flex items-center justify-center z-50"
          style={{ 
            background: 'rgba(0,0,0,0.55)', 
            backdropFilter: 'blur(6px)',
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation();
            startGame();
          }}
        >
          <div 
            className="text-center" 
            style={{ maxWidth: 320, pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-3xl font-bold text-white mb-4">Gotowy na grę?</h2>
            <p className="text-white/80 mb-6 text-sm">
              Kliknij przycisk start lub użyj strzałek, aby zebrać 6 liczb.
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                startGame();
              }}
              className="px-6 py-3 rounded-full text-white font-semibold cursor-pointer hover:opacity-90 transition-opacity"
              style={{
                background: 'linear-gradient(90deg, #34d399 0%, #10b981 100%)',
                boxShadow: '0 10px 25px rgba(16, 185, 129, 0.35)',
                pointerEvents: 'auto'
              }}
            >
              ▶️ Startuj!
            </button>
          </div>
        </div>
      )}
      {/* Nagłówek */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="text-center mb-6"
      >
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-400 via-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
          🐍 Snake Lotto
        </h1>
        <p className="text-white/80 text-lg">
          Zbierz 6 liczb sterując wężem!
        </p>
      </motion.div>

      {/* Statystyki gry */}
      <div className="flex gap-4 mb-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
          <span className="text-white/70 text-sm">Zebrane: </span>
          <span className="text-white font-bold">{collectedNumbers.length}/6</span>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
          <span className="text-white/70 text-sm">Punkty: </span>
          <span className="text-white font-bold">{score}</span>
        </div>
      </div>

      {/* Plansza gry */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="relative bg-black/30 backdrop-blur-sm rounded-2xl p-4 border border-white/20 mb-6"
        style={{
          width: 'clamp(260px, 90vw, 420px)',
          height: 'auto',
          aspectRatio: '1 / 1',
          touchAction: 'none', // Blokuj scrollowanie na planszy
          userSelect: 'none'   // Blokuj zaznaczanie
        }}
      >
        <div 
          className="relative w-full h-full grid gap-1"
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridTemplateRows: `repeat(${gridSize}, 1fr)`
          }}
        >
          {/* Siatka gry */}
          {Array.from({ length: gridSize * gridSize }, (_, i) => {
            const x = i % gridSize;
            const y = Math.floor(i / gridSize);
            const isSnake = renderSnake.some(segment => segment.x === x && segment.y === y);
            const isHead = renderSnake[0] && renderSnake[0].x === x && renderSnake[0].y === y;
            const isFood = renderFood.x === x && renderFood.y === y;
            
            return (
              <div
                key={i}
                className={`relative border border-white/10 ${
                  isSnake 
                    ? isHead 
                      ? 'bg-gradient-to-br from-green-400 to-green-600' 
                      : 'bg-gradient-to-br from-green-500 to-green-700'
                    : 'bg-transparent'
                }`}
              >
                {/* Jabłko z numerem */}
                {isFood && (
                  <motion.div
                    initial={{ scale: 0, rotate: 0 }}
                    animate={{ 
                      scale: 1, 
                      rotate: renderFood.isHidden ? 1080 : renderFood.isSpecial ? 720 : renderFood.isGolden ? 360 : 0 
                    }}
                    className={`absolute inset-0 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                      renderFood.isHidden
                        ? 'bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 shadow-lg shadow-cyan-500/50'
                        : renderFood.isSpecial
                          ? 'bg-gradient-to-br from-purple-400 via-pink-500 to-purple-600 shadow-lg shadow-purple-500/50'
                          : renderFood.isGolden 
                            ? 'bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg shadow-yellow-500/50' 
                            : 'bg-gradient-to-br from-red-500 to-red-700'
                    }`}
                  >
                    {/* Efekt glow dla specjalnych jabłek */}
                    {(renderFood.isGolden || renderFood.isSpecial || renderFood.isHidden) && (
                      <motion.div
                        className={`absolute inset-0 rounded-full ${
                          renderFood.isHidden ? 'bg-cyan-400/30' :
                          renderFood.isSpecial ? 'bg-purple-400/30' : 'bg-yellow-400/30'
                        }`}
                        animate={{
                          scale: [1, 1.4, 1],
                          opacity: [0.3, 0, 0.3]
                        }}
                        transition={{
                          duration: renderFood.isHidden ? 0.6 : renderFood.isSpecial ? 0.8 : 1.2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    )}
                    
                    {/* Ikona dla jabłek */}
                    <span className="relative z-10">
                      {renderFood.isHidden ? '❓' : renderFood.isSpecial ? '⭐' : renderFood.number}
                    </span>
                  </motion.div>
                )}
                
                {/* Głowa węża z oczami */}
                {isHead && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    animate={{
                      scale: [1, 1.1, 1]
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <div className="text-xs">👀</div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>

      </motion.div>

      {/* Zebrane liczby - podgląd */}
      {gameState === 'playing' && collectedNumbers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4"
        >
          <p className="text-white/70 text-sm text-center mb-2">Zebrane liczby:</p>
          <div className="flex justify-center gap-2 flex-wrap">
            {collectedNumbers.map((number, index) => (
              <motion.div
                key={number}
                initial={{ scale: 0, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-lg border border-white/30"
              >
                {number}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Instrukcje sterowania */}
      {gameState === 'waiting' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mb-6"
        >
          <p className="text-white/90 text-lg mb-2">🎮 Jak grać:</p>
          <p className="text-white/70 text-sm mb-1">🖥️ Desktop: Klawisze strzałek</p>
          <p className="text-white/70 text-sm mb-4">📱 Mobile: Trzymaj palec i przeciągnij w kierunku</p>
          
          <motion.button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              startGame();
            }}
            className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 active:from-green-700 active:to-blue-700 text-white font-bold py-4 px-10 rounded-full shadow-lg border-2 border-green-400/30 cursor-pointer select-none"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
          >
            🚀 Rozpocznij grę!
          </motion.button>
        </motion.div>
      )}

      {/* Instrukcje podczas gry */}
      {gameState === 'playing' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <p className="text-white/80 text-sm">
            Zbierz {6 - collectedNumbers.length} liczb!
          </p>
          <div className="flex gap-2 justify-center mt-2 flex-wrap">
            <p className="text-red-400 text-xs">🔴 Zwykłe = 10 pkt</p>
            <p className="text-yellow-400 text-xs">🟡 Złote = 50 pkt</p>
            <p className="text-purple-400 text-xs">⭐ Gwiazda = 100 pkt</p>
            <p className="text-cyan-400 text-xs">❓ Ukryte = 150 pkt</p>
          </div>
        </motion.div>
      )}


      {/* Przycisk reset podczas gry */}
      {gameState === 'playing' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-4"
        >
          <motion.button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              initGame();
            }}
            className="bg-red-500/30 hover:bg-red-500/40 active:bg-red-500/50 text-red-200 font-bold py-3 px-6 rounded-full border-2 border-red-400/50 transition-all duration-200 cursor-pointer select-none"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
          >
            🔄 Reset gry
          </motion.button>
        </motion.div>
      )}

      {/* Game Over */}
      <AnimatePresence>
        {gameState === 'gameOver' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: [0, -5, 5, -5, 5, 0] }}
              transition={{ duration: 0.5 }}
              className="mb-4"
            >
              <h2 className="text-3xl font-bold text-red-400 mb-2">💥 Game Over!</h2>
              <p className="text-white/80">Uderzyłeś w ścianę lub siebie!</p>
            </motion.div>
            
            <div className="mb-6">
              <p className="text-white/70 mb-2">Zebrane liczby: {collectedNumbers.length}/6</p>
              <p className="text-yellow-400 font-bold">Punkty: {score}</p>
            </div>

            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              onClick={initGame}
              className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-full shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🔄 Spróbuj ponownie
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wyniki końcowe */}
      <AnimatePresence>
        {gameState === 'finished' && finalNumbers.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="text-center"
          >
            <motion.h2
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="text-2xl font-bold text-white mb-6"
            >
              🎉 Twoje liczby Lotto!
            </motion.h2>
            
            {/* Kulki z wynikami - klasyczne żółte kulki lotto */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {finalNumbers.map((number, index) => (
                <motion.div
                  key={number}
                  initial={{ 
                    scale: 0, 
                    y: 100,
                    rotate: 0 
                  }}
                  animate={{ 
                    scale: 1, 
                    y: 0,
                    rotate: 360 
                  }}
                  transition={{
                    delay: index * 0.2,
                    duration: 0.8,
                    type: "spring",
                    stiffness: 100
                  }}
                  className="relative"
                >
                  <motion.div
                    animate={{
                      boxShadow: [
                        "0 0 15px rgba(255, 215, 0, 0.4)",
                        "0 0 25px rgba(255, 215, 0, 0.6)",
                        "0 0 15px rgba(255, 215, 0, 0.4)"
                      ]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="w-16 h-16 bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 rounded-full flex items-center justify-center text-black font-bold text-xl border-2 border-yellow-300/50 relative overflow-hidden"
                  >
                    {/* Efekt shine - subtelny */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                      animate={{
                        x: ["-100%", "100%"],
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatDelay: 2,
                        ease: "easeInOut"
                      }}
                      style={{
                        transform: "skewX(-20deg)",
                      }}
                    />
                    <span className="relative z-10 drop-shadow-sm">{number}</span>
                  </motion.div>
                </motion.div>
              ))}
            </div>

            {/* Przyciski akcji */}
            <div className="flex gap-3 justify-center mb-4">
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  copyNumbers();
                }}
                className="bg-white/30 hover:bg-white/40 active:bg-white/50 text-white font-bold py-3 px-5 rounded-full border-2 border-white/40 transition-all duration-200 flex items-center space-x-2 cursor-pointer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
              >
                <span>📋</span>
                <span>Kopiuj</span>
              </motion.button>
              
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.7 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToFavorites();
                }}
                className="bg-yellow-500/30 hover:bg-yellow-500/40 active:bg-yellow-500/50 text-white font-bold py-3 px-5 rounded-full border-2 border-yellow-400/40 transition-all duration-200 flex items-center space-x-2 cursor-pointer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
              >
                <span>⭐</span>
                <span>Ulubione</span>
              </motion.button>
              
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.9 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  initGame();
                }}
                className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 active:from-green-700 active:to-blue-700 text-white font-bold py-3 px-5 rounded-full border-2 border-green-400/40 cursor-pointer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
              >
                🔄 Nowa gra
              </motion.button>
            </div>

            <p className="text-white/70 text-sm">
              Punkty: <span className="font-bold text-yellow-400">{score}</span>
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Konfetti */}
      <AnimatePresence>
        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none overflow-hidden">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  y: -50,
                  x: Math.random() * window.innerWidth,
                  rotate: 0,
                  scale: 0
                }}
                animate={{
                  y: window.innerHeight + 50,
                  rotate: 360,
                  scale: [0, 1, 0]
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.1,
                  ease: "easeOut"
                }}
                className="absolute"
              >
                <div className={`w-3 h-3 ${
                  i % 4 === 0 ? 'bg-yellow-400' :
                  i % 4 === 1 ? 'bg-green-400' :
                  i % 4 === 2 ? 'bg-blue-400' : 'bg-purple-400'
                } rounded-full`} />
              </motion.div>
            ))}
          </div>
        )}
      </AnimatePresence>

      {/* Efekt przy złapaniu złotej kuli */}
      {renderFood.isGolden && gameState === 'playing' && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            background: [
              "radial-gradient(circle, rgba(255, 215, 0, 0) 0%, rgba(255, 215, 0, 0) 100%)",
              "radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, rgba(255, 215, 0, 0) 70%)",
              "radial-gradient(circle, rgba(255, 215, 0, 0) 0%, rgba(255, 215, 0, 0) 100%)"
            ]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}

      {/* Sparkle efekty w tle */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 1, 0],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default SnakeLotto;
